import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Globe,
  Upload,
  Loader2,
  Trash2,
  FileText,
  FileSpreadsheet,
  Presentation,
  Image as ImageIcon,
  Info,
} from 'lucide-react';
import {
  useGlobalKnowledge,
  useGlobalKnowledgeStats,
  useUploadGlobalFile,
  useDeleteGlobalFile,
} from '@/hooks/useGlobalKnowledge';
import { usePlatformSetting, useUpdatePlatformSetting } from '@/hooks/usePlatformSettings';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ACCEPT_ALL,
  formatFileSize,
  isSupportedExtension,
  validateUploadSize,
} from '@/lib/fileConstants';
import { toast } from 'sonner';

// Conservative ceiling for global knowledge: every token here is paid on
// every turn of every user in the Espaço Caldeira. Smaller is better.
const MAX_GLOBAL_FILES = 10;
const MAX_GLOBAL_TOKENS = 50_000;

function getFileIcon(fileType: string) {
  switch (fileType) {
    case 'xlsx':
    case 'csv':
      return <FileSpreadsheet className="w-4 h-4 text-green-500 shrink-0" />;
    case 'pptx':
      return <Presentation className="w-4 h-4 text-orange-500 shrink-0" />;
    case 'image':
      return <ImageIcon className="w-4 h-4 text-purple-500 shrink-0" />;
    default:
      return <FileText className="w-4 h-4 text-blue-500 shrink-0" />;
  }
}

function progressColor(ratio: number): string {
  if (ratio >= 1) return 'bg-red-500';
  if (ratio >= 0.6) return 'bg-yellow-500';
  return 'bg-brand-primary';
}

export const EspacoCaldeiraPanel = () => {
  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2">
        <Globe className="h-4 w-4 text-brand-primary" />
        <h3 className="text-sm font-semibold text-primary">Espaço Caldeira</h3>
      </div>

      <div className="rounded-lg border border-color bg-secondary/50 p-3 flex gap-2 text-xs text-secondary">
        <Info className="w-4 h-4 shrink-0 mt-0.5 text-brand-primary" />
        <p>
          As instruções e arquivos abaixo ficam disponíveis em <strong>todas as conversas do Espaço Caldeira</strong> — chat solto, com agente, e dentro de projetos. Conversas pessoais não herdam esse contexto. Cada token aqui é paga em todo turno de todo usuário, então mantenha enxuto.
        </p>
      </div>

      <SystemPromptSection />

      <div className="border-t border-color" />

      <KnowledgeFilesSection />
    </div>
  );
};

// ============================================================
// Editor do system prompt global
// ============================================================

const SystemPromptSection = () => {
  const { data: currentValue, isLoading } = usePlatformSetting<string>('global_system_prompt');
  const updateSetting = useUpdatePlatformSetting();
  const [draft, setDraft] = useState<string>('');

  useEffect(() => {
    if (typeof currentValue === 'string') setDraft(currentValue);
  }, [currentValue]);

  const hasChanges = draft !== (currentValue ?? '');
  const charCount = draft.length;
  const estimatedTokens = Math.round(draft.split(/\s+/).filter(Boolean).length * 1.3);

  const handleSave = () => {
    updateSetting.mutate({ key: 'global_system_prompt', value: draft });
  };

  const handleReset = () => {
    setDraft(currentValue ?? '');
  };

  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="text-sm font-medium text-primary">Instruções globais</label>
        <p className="text-xs text-tertiary mt-0.5">
          Texto que prefixa o system prompt em todas as conversas do Espaço Caldeira. Pode deixar vazio.
        </p>
      </div>
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder='Ex: "Você é um assistente do Instituto Caldeira. Use o contexto institucional dos arquivos abaixo quando relevante."'
        className="min-h-[120px] font-mono text-sm"
      />
      <div className="flex items-center justify-between">
        <p className="text-xs text-tertiary">
          {charCount.toLocaleString()} caracteres • ~{estimatedTokens.toLocaleString()} tokens
        </p>
        {hasChanges && (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={handleReset} disabled={updateSetting.isPending}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleSave} disabled={updateSetting.isPending}>
              {updateSetting.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Lista, upload e delete de arquivos de conhecimento global
// ============================================================

const KnowledgeFilesSection = () => {
  const { data: files, isLoading } = useGlobalKnowledge();
  const { data: stats } = useGlobalKnowledgeStats();
  const uploadFile = useUploadGlobalFile();
  const deleteFile = useDeleteGlobalFile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!isSupportedExtension(file.name)) {
        toast.error(`Tipo de arquivo não suportado: .${file.name.split('.').pop()}`);
        return;
      }
      const sizeError = validateUploadSize(file);
      if (sizeError) {
        toast.error(sizeError);
        return;
      }
      if ((files?.length ?? 0) >= MAX_GLOBAL_FILES) {
        toast.error(`Limite de ${MAX_GLOBAL_FILES} arquivos atingido`);
        return;
      }
      uploadFile.mutate(
        { file },
        {
          onSuccess: () => toast.success(`"${file.name}" enviado`),
          onError: (err) => toast.error(`Erro ao enviar: ${err.message}`),
        },
      );
    },
    [files?.length, uploadFile],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      Array.from(e.dataTransfer.files).forEach((f) => handleFileSelect(f));
    },
    [handleFileSelect],
  );

  const handleDeleteFile = useCallback(
    (fileId: string, storagePath: string, fileName: string) => {
      deleteFile.mutate(
        { fileId, storagePath },
        {
          onSuccess: () => toast.success(`"${fileName}" removido`),
          onError: (err) => toast.error(`Erro ao remover: ${err.message}`),
        },
      );
    },
    [deleteFile],
  );

  const fileCount = stats?.file_count ?? 0;
  const totalTokens = stats?.total_tokens ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-medium text-primary">Arquivos de contexto</h4>
        <p className="text-xs text-tertiary mt-0.5">
          PDF, DOCX, PPTX, XLSX, TXT, MD, CSV ou imagens. Recomendamos manter o total bem abaixo de {MAX_GLOBAL_TOKENS.toLocaleString()} tokens.
        </p>
      </div>

      {/* Usage bars */}
      <div className="space-y-3">
        <div>
          <div className="flex justify-between text-xs text-secondary mb-1">
            <span>Arquivos</span>
            <span>
              {fileCount} / {MAX_GLOBAL_FILES}
            </span>
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${progressColor(Number(fileCount) / MAX_GLOBAL_FILES)}`}
              style={{ width: `${Math.min(100, (Number(fileCount) / MAX_GLOBAL_FILES) * 100)}%` }}
            />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-xs text-secondary mb-1">
            <span>Tokens</span>
            <span>
              {Number(totalTokens).toLocaleString()} / {MAX_GLOBAL_TOKENS.toLocaleString()}
            </span>
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${progressColor(Number(totalTokens) / MAX_GLOBAL_TOKENS)}`}
              style={{ width: `${Math.min(100, (Number(totalTokens) / MAX_GLOBAL_TOKENS) * 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Dropzone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`flex flex-col items-center justify-center gap-2 px-4 py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
          isDragging ? 'border-brand-primary bg-hover' : 'border-color hover:bg-hover'
        }`}
      >
        <Upload className={`w-8 h-8 ${isDragging ? 'text-brand-primary' : 'text-secondary'}`} />
        <span className="text-sm text-secondary">Arraste arquivos ou clique para selecionar</span>
        <span className="text-xs text-tertiary">PDF, DOCX, PPTX, XLSX, TXT, MD, CSV, PNG, JPG, GIF, WebP — máx. 128 MB</span>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_ALL}
          multiple
          className="hidden"
          onChange={(e) => {
            Array.from(e.target.files ?? []).forEach((f) => handleFileSelect(f));
            e.target.value = '';
          }}
        />
      </div>

      {/* File list */}
      <div>
        <label className="block text-sm font-medium text-primary mb-2">
          Arquivos ({files?.length ?? 0})
        </label>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-tertiary" />
          </div>
        ) : !files?.length ? (
          <p className="text-sm text-secondary text-center py-6">
            Nenhum arquivo de contexto global ainda
          </p>
        ) : (
          <div className="space-y-1.5">
            {files.map((file) => (
              <div
                key={file.id}
                className="group flex items-center gap-2 p-2.5 rounded-lg bg-secondary hover:bg-hover transition-colors"
              >
                {getFileIcon(file.file_type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-primary truncate">{file.file_name}</p>
                  <p className="text-xs text-tertiary">{formatFileSize(file.file_size_bytes)}</p>
                </div>
                <TooltipProvider>
                  {file.parsing_status === 'pending' && (
                    <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-600 border-yellow-500/30 shrink-0">
                      Pendente
                    </Badge>
                  )}
                  {file.parsing_status === 'processing' && (
                    <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 border-blue-500/30 animate-pulse shrink-0">
                      Processando
                    </Badge>
                  )}
                  {file.parsing_status === 'completed' && (
                    <Badge variant="outline" className="text-xs bg-green-500/10 text-green-600 border-green-500/30 shrink-0">
                      {file.token_count?.toLocaleString() ?? 0} tokens
                    </Badge>
                  )}
                  {file.parsing_status === 'ready' && (
                    <Badge variant="outline" className="text-xs bg-green-500/10 text-green-600 border-green-500/30 shrink-0">
                      Pronto
                    </Badge>
                  )}
                  {file.parsing_status === 'failed' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="text-xs bg-red-500/10 text-red-600 border-red-500/30 cursor-help shrink-0">
                          Falhou
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-[200px]">
                        <p className="text-xs">{file.parsing_error || 'Erro desconhecido'}</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TooltipProvider>
                <button
                  onClick={() => handleDeleteFile(file.id, file.storage_path, file.file_name)}
                  className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-active transition-all"
                  disabled={deleteFile.isPending}
                  title="Remover"
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
